/* eslint-disable react/display-name */
import classNames from 'classnames'
import { RootState } from 'duck/store'
import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { showOrganizationDetailsAsAdmin } from 'scenes/Dashboard/components/OrganizationDetailsModal/duck/actions'
import { displayOrganizationTypes, getOrganizationLastFundingRound } from 'scenes/Dashboard/data/organizations/helpers'
import { getOrganization } from 'scenes/Dashboard/data/organizations/selectors'
import { Organization } from 'scenes/Dashboard/data/organizations/types'
import { isOrganizationBeingAnalyzed } from 'scenes/Dashboard/scenes/OrganizationsManager/duck/selectors'
import { makeStyles } from 'tss-react/mui'

import { Hidden, TableRow, Theme, Typography, useTheme } from '@mui/material'

import OrganizationLogo from '../../../../../../components/OrganizationLogo'
import RowCell, { RowType } from '../../../../../../components/RowCell'

export type OrganizationRowProps = {
  organizationId?: Organization['id']
  className?: string
  style?: React.CSSProperties
}

type OrganizationRowData = {
  header: boolean
  organization: Organization
  isBeingAnalyzed?: boolean
}

type OrganizationRowHandlers = {
  showOrganizationDetails: () => any
}

type GenerateRowProps = {
  headerContent: React.ReactNode
  content: React.ReactNode
  colSpan: number
  centered?: boolean
  onClick?: () => any
}

const getLastFundingEvent = (organization: Organization) => {
  const lastFundingRound = getOrganizationLastFundingRound(organization)
  return lastFundingRound ? lastFundingRound.roundType : '-'
}

function useOrganizationRowData(organizationId?: Organization['id']): OrganizationRowData {
  const organization = useSelector((state: RootState) => getOrganization(state, organizationId))
  const isBeingAnalyzed = useSelector((state: RootState) => isOrganizationBeingAnalyzed(state, organizationId))

  return {
    header: !organization,
    organization: organization || ({} as Organization),
    isBeingAnalyzed,
  }
}

function useOrganizationRowHandlers(organizationId?: Organization['id']): OrganizationRowHandlers {
  const dispatch = useDispatch()
  const showOrganizationDetails = useCallback(() => {
    dispatch(showOrganizationDetailsAsAdmin({ organizationId }))
  }, [dispatch, organizationId])

  return {
    showOrganizationDetails,
  }
}

export const OrganizationRow: React.FunctionComponent<OrganizationRowProps> = React.memo(
  ({ className, style, organizationId }) => {
    const { classes } = useStyles()
    const theme = useTheme()
    const { header, organization, isBeingAnalyzed } = useOrganizationRowData(organizationId)
    const { showOrganizationDetails } = useOrganizationRowHandlers(organizationId)
    const { id, name, domain } = organization

    const generateRow = useCallback(
      ({ headerContent, content, ...otherProps }: GenerateRowProps) => {
        const rowType = header ? RowType.header : RowType.default
        return (
          <RowCell
            className={classNames({ [classes.unavailableCell]: isBeingAnalyzed })}
            rowType={rowType}
            onClick={showOrganizationDetails}
            contentByRowType={{
              [RowType.header]: headerContent,
              [RowType.default]: content,
            }}
            {...otherProps}
          />
        )
      },
      [classes.unavailableCell, header, isBeingAnalyzed, showOrganizationDetails]
    )

    const organizationIdCell = useMemo(
      () =>
        generateRow({
          colSpan: 1,
          headerContent: 'Id',
          content: (
            <div>
              <Typography>{id}</Typography>
            </div>
          ),
        }),
      [generateRow, id]
    )

    const organizationLogoCell = useMemo(
      () =>
        generateRow({
          colSpan: 2,
          centered: true,
          headerContent: '',
          content: <OrganizationLogo organization={organization} size={theme.spacing(6)} />,
        }),
      [generateRow, organization, theme]
    )

    const organizationNameCell = useMemo(
      () =>
        generateRow({
          colSpan: 3,
          headerContent: 'Organization',
          content: (
            <div className={classes.organizationNameContainer}>
              <Typography variant="subtitle1">{name || domain || '–'}</Typography>
              {name && domain ? <Typography variant="caption">{domain}</Typography> : null}
            </div>
          ),
        }),
      [classes.organizationNameContainer, domain, generateRow, name]
    )

    const typesCell = useMemo(
      () =>
        generateRow({
          colSpan: 3,
          headerContent: 'Types',
          content: displayOrganizationTypes(organization) || '–',
        }),
      [generateRow, organization]
    )

    const stageCell = useMemo(() => {
      const lastFundingRound = getLastFundingEvent(organization)
      return generateRow({
        colSpan: 3,
        headerContent: 'Last funding event',
        content: lastFundingRound,
      })
    }, [generateRow, organization])

    const processedByOpsCell = useMemo(
      () =>
        generateRow({
          colSpan: 3,
          headerContent: 'Manually edited',
          content: organization.processedByOps ? 'True' : '–',
        }),
      [generateRow, organization]
    )

    return (
      <TableRow key={id} style={style} className={classNames(className, classes.row)} hover={!header}>
        {organizationIdCell}
        {organizationLogoCell}
        {organizationNameCell}
        {typesCell}
        <Hidden mdDown>{stageCell}</Hidden>
        {processedByOpsCell}
      </TableRow>
    )
  }
)

const useStyles = makeStyles()(({ palette, spacing }: Theme) => ({
  organizationNameContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  placeHolderRow: {
    backgroundColor: palette.grey['100'],
  },
  row: {
    transition: 'all 0.1s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  logoCell: {
    paddingLeft: spacing(2),
    paddingRight: 0,
  },
  unavailableCell: {
    filter: 'blur(3px)',
  },
  loadingMessage: {
    marginRight: spacing(2),
  },
}))
